// API service for backend communication
// Prefer relative URLs so CRA `proxy` in package.json forwards to the backend.
// Override with REACT_APP_API_URL only when you *explicitly* want an absolute base.
const API_URL = process.env.REACT_APP_API_URL || '';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('token');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');
  }

  // Keep token in sync with storage across refreshes/hot reloads/multi-tab usage.
  // Returns the best-known token (in-memory preferred, else localStorage).
  _rehydrateTokenFromStorage() {
    const stored = localStorage.getItem('token');
    const hydrated = this.token || stored;
    if (hydrated && hydrated !== this.token) {
      this.token = hydrated;
    }
    return hydrated;
  }

  _devLog(...args) {
    // Only log in non-production builds.
    if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
      // eslint-disable-next-line no-console
      console.debug(...args);
    }
  }
  
  setToken(token) {
    this.token = token;
    localStorage.setItem('token', token);
  }
  
  setUser(user) {
    this.user = user;
    localStorage.setItem('user', JSON.stringify(user));
  }
  
  clearToken() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }
  
  // Generic HTTP methods for flexible API calls
  async get(endpoint) {
    return this.request(endpoint, { method: 'GET' });
  }
  
  async post(endpoint, body) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  
  async put(endpoint, body) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }
  
  async delete(endpoint) {
    return this.request(endpoint, { method: 'DELETE' });
  }
  
  async request(endpoint, options = {}) {
    const url = `${API_URL}${endpoint}`;

    // Always rehydrate token on each request to avoid stale in-memory state.
    const hydratedToken = this._rehydrateTokenFromStorage();

    const config = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    };

    // Preserve any explicit Authorization provided by the caller, but if none
    // is present and we have a token, inject it.
    const existingAuthHeader =
      config.headers?.Authorization ||
      config.headers?.authorization ||
      config.headers?.AUTHORIZATION;

    if (!existingAuthHeader && hydratedToken) {
      config.headers['Authorization'] = `Bearer ${hydratedToken}`;
    }

    this._devLog('[api] request', {
      endpoint,
      method: (config.method || 'GET').toUpperCase(),
      hasToken: !!hydratedToken,
      hasAuthHeader: !!(existingAuthHeader || config.headers?.Authorization),
    });

    const doFetch = (fetchUrl) => fetch(fetchUrl, config);

    let response = await doFetch(url);

    // Robust 401 handling:
    // - Browsers may drop Authorization headers on redirects.
    // - In-memory token can become stale vs localStorage.
    // Pattern: on 401, rehydrate token and retry *once* before surfacing.
    if (response.status === 401) {
      const token2 = this._rehydrateTokenFromStorage();
      if (token2) {
        config.headers['Authorization'] = `Bearer ${token2}`;
      }

      // If the response was redirected, retry against the final URL to avoid auth dropping.
      const retryUrl = (response.redirected && response.url) ? response.url : url;

      this._devLog('[api] 401 retry-once', {
        endpoint,
        originalUrl: url,
        retryUrl,
        hadToken: !!token2,
      });

      response = await doFetch(retryUrl);

      if (response.status === 401) {
        // Do NOT clear the token aggressively here; a single 401 can be caused by
        // redirects/proxies or transient client state. Let the UI route to login.
        throw new Error('Session expired, please log in again');
      }
    }
    
    if (response.status === 402) {
      const error = await response.json().catch(() => ({ detail: 'account_locked' }));
      if ((error.detail || '') === 'account_locked') {
        this.clearToken();
        window.dispatchEvent(new CustomEvent('vinnie:account_locked'));
      }
      throw new Error('account_locked');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }
    
    return response.json();
  }
  
  // Auth
  async login(email, password) {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Login failed' }));
      throw new Error(err.detail || 'Login failed');
    }
    
    const data = await response.json();
    this.setToken(data.access_token);

    // Do not block login on profile fetch.
    // App() already calls loadUser() after isLoggedIn flips true.
    
    return data;
  }
  
  async register(email, password, fullName) {
    return this.request('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, full_name: fullName }),
    });
  }

  async resetPassword(token, newPassword) {
    const url = `${API_URL}/api/auth/reset-password`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, new_password: newPassword }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ detail: 'Reset failed' }));
      throw new Error(err.detail || 'Reset failed');
    }
    return response.json();
  }
  
  async getMe() {
    return this.request('/api/auth/me');
  }
  
  async updateProfile(profile) {
    return this.request('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(profile),
    });
  }
  
  // Contacts
  async getContacts(search = '') {
    const query = search ? `?search=${encodeURIComponent(search)}` : '';
    return this.request(`/api/contacts/${query}`);
  }
  
  async getContact(contactId) {
    return this.request(`/api/contacts/${contactId}`);
  }

  async regenerateLeadBrief(contactId) {
    return this.request(`/api/contacts/${contactId}/lead-brief/regenerate`, {
      method: 'POST',
    });
  }
  async createContact(contact) {
    return this.request('/api/contacts/', {
      method: 'POST',
      body: JSON.stringify(contact),
    });
  }
  
  async updateContact(contactId, contact) {
    return this.request(`/api/contacts/${contactId}`, {
      method: 'PUT',
      body: JSON.stringify(contact),
    });
  }

  // Partial contact update (preferred for in-place edits)
  async patchContact(contactId, patch) {
    return this.request(`/api/contacts/${contactId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }
  
  async deleteContact(contactId) {
    return this.request(`/api/contacts/${contactId}`, {
      method: 'DELETE',
    });
  }

  // Activities
  async getContactActivities(contactId) {
    return this.request(`/api/contacts/${contactId}/activities`);
  }

  
  async getContactConversations(contactId) {
    return this.request(`/api/contacts/${contactId}/conversations`);
  }

  async getContactLeadActivities(contactId) {
    return this.request(`/api/contacts/${contactId}/lead-activities`);
  }

  async getContactLeadSnapshot(contactId) {
    return this.request(`/api/contacts/${contactId}/lead-snapshot`);
  }


  async createActivity(contactId, activity) {
    return this.request(`/api/contacts/${contactId}/activities`, {
      method: 'POST',
      body: JSON.stringify(activity),
    });
  }

  // Voice
  async processVoice(audioBlob) {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    
    const token = this._rehydrateTokenFromStorage();
    const url = `${API_URL}/api/voice/process`;

    const makeReq = (tkn) => fetch(url, {
      method: 'POST',
      headers: tkn ? { 'Authorization': `Bearer ${tkn}` } : {},
      body: formData,
    });

    let response = await makeReq(token);

    if (response.status === 401) {
      const token2 = this._rehydrateTokenFromStorage();
      this._devLog('[api] voice/process 401 retry-once', { hasToken: !!token2 });
      response = await makeReq(token2);
      if (response.status === 401) {
        throw new Error('Session expired, please log in again');
      }
    }
    
    if (!response.ok) {
      throw new Error('Voice processing failed');
    }
    
    return response.json();
  }

  async executeVoiceIntent({ intent, intentType, parameters, contactId }) {
    return this.request('/api/voice/execute', {
      method: 'POST',
      body: JSON.stringify({
        intent: intent || undefined,
        intent_type: intentType || undefined,
        parameters: parameters || undefined,
        contact_id: contactId || undefined,
      }),
    });
  }

  async confirmCreateContact(proposedContact) {
    return this.request('/api/voice/confirm_create_contact', {
      method: 'POST',
      body: JSON.stringify({
        proposed_contact: proposedContact || {},
      }),
    });
  }

  // ============================================
  // ADMIN ENDPOINTS
  // ============================================
  
  // Dealer Profile
  async getDealerProfile() {
    return this.request('/api/admin/dealer');
  }
  
  async updateDealerProfile(profile) {
    return this.request('/api/admin/dealer', {
      method: 'PUT',
      body: JSON.stringify(profile),
    });
  }

  // Comms (read-only for dealer admins)
  async getCommsHealth() {
    return this.request('/api/admin/comms/health');
  }

  async getAdvancedComms() {
    return this.request('/api/admin/comms/advanced');
  }
  
  // Knowledge Base
  async getKnowledgeDocs() {
    return this.request('/api/admin/knowledge');
  }

  // Sites
  async getSites() {
    return this.request('/api/admin/sites');
  }

  async createSite(payload) {
    return this.request('/api/admin/sites', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSite(siteId, payload) {
    return this.request(`/api/admin/sites/${siteId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async rotateSiteKeys(siteId) {
    return this.request(`/api/admin/sites/${siteId}/rotate-keys`, {
      method: 'POST',
    });
  }

  // Site inbound numbers (Twilio inbound routing)
  async listSiteInboundNumbers(siteId) {
    return this.request(`/api/admin/sites/${siteId}/inbound-numbers`);
  }

  async createSiteInboundNumber(siteId, payload) {
    return this.request(`/api/admin/sites/${siteId}/inbound-numbers`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteSiteInboundNumber(siteId, routeId) {
    return this.request(`/api/admin/sites/${siteId}/inbound-numbers/${routeId}`, {
      method: 'DELETE',
    });
  }

  // Email Suppression
  async listEmailSuppressionRules() {
    return this.request('/api/admin/email-suppression');
  }

  async createEmailSuppressionRule(payload) {
    return this.request('/api/admin/email-suppression', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteEmailSuppressionRule(ruleId) {
    return this.request(`/api/admin/email-suppression/${ruleId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // PLATFORM (SUPER ADMIN)
  // ============================================

  async bootstrapSuperAdmin(payload) {
    // Intentionally unauthenticated (token omitted if none)
    return this.request('/api/platform/bootstrap/super-admin', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }


  async getPlatformSettings() {
    return this.request('/api/platform/settings');
  }

  async updatePlatformSettings(payload) {
    return this.request('/api/platform/settings', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  // ============================================
  // SUPER ADMIN: DEALERS + SMS CONFIGS
  // ============================================

  // Dealer Groups (tenant provisioning) — super_admin only
  async listDealers() {
    // Back-compat: returns DealerResponse[]
    return this.request('/api/admin/dealers');
  }

  async listDealerGroups() {
    return this.request('/api/admin/dealer-groups');
  }

  async createDealerGroupWizard(payload) {
    return this.request('/api/admin/dealer-groups/wizard', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async getDealerSmsConfigs(dealerId) {
    return this.request(`/api/admin/dealers/${dealerId}/sms-configs`);
  }

  async upsertDealerSmsConfig(dealerId, payload) {
    return this.request(`/api/admin/dealers/${dealerId}/sms-configs`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async sendDealerTestSms(dealerId, payload) {
    return this.request(`/api/admin/dealers/${dealerId}/sms-configs/test-sms`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ============================================
  // AUTOPILOT (CONTACT-LEVEL)
  // ============================================

  async updateContactAutopilot(contactId, payload) {
    return this.request(`/api/contacts/${contactId}/autopilot`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async getContactOutboundMessages(contactId) {
    return this.request(`/api/contacts/${contactId}/autopilot/outbound`);
  }

  async getContactSmsThread(contactId, params = {}) {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.before) qs.set('before', String(params.before));
    const q = qs.toString();
    return this.request(`/api/contacts/${contactId}/autopilot/sms-thread${q ? `?${q}` : ''}`);
  }

  async getContactEmailThread(contactId, params = {}) {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.before) qs.set('before', String(params.before));
    const q = qs.toString();
    return this.request(`/api/contacts/${contactId}/autopilot/email-thread${q ? `?${q}` : ''}`);
  }

  async getContactUnifiedThread(contactId, params = {}) {
    const qs = new URLSearchParams();
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.before) qs.set('before', String(params.before));
    if (params?.view) qs.set('view', String(params.view));
    const q = qs.toString();
    return this.request(`/api/contacts/${contactId}/autopilot/unified-thread${q ? `?${q}` : ''}`);
  }

  async getContactPlannedMessages(contactId, params = {}) {
    const qs = new URLSearchParams();
    if (params?.channel) qs.set('channel', String(params.channel));
    const q = qs.toString();
    return this.request(`/api/contacts/${contactId}/follow-up/planned-messages${q ? `?${q}` : ''}`);
  }

  async getContactFollowUpPlanPreview(contactId, intensity) {
    const qs = new URLSearchParams();
    if (typeof intensity === 'number') qs.set('intensity', String(intensity));
    const q = qs.toString();
    return this.request(`/api/contacts/${contactId}/follow-up/plan-preview${q ? `?${q}` : ''}`);
  }

  async applyContactFollowUpPlan(contactId, intensity) {
    return this.request(`/api/contacts/${contactId}/follow-up/apply`, {
      method: 'POST',
      body: JSON.stringify({ intensity }),
    });
  }

  async cancelContactPlannedMessage(contactId, plannedMessageId) {
    return this.request(`/api/contacts/${contactId}/follow-up/planned-messages/${plannedMessageId}/cancel`, {
      method: 'POST',
    });
  }

  // ============================================
  // OUTBOUND COMPOSER (MANUAL)
  // ============================================

  async draftOutboundEmail(contactId) {
    return this.request(`/api/contacts/${contactId}/outbound/email/draft`, {
      method: 'POST',
    });
  }

  async sendOutboundEmail(contactId, payload) {
    return this.request(`/api/contacts/${contactId}/outbound/email/send`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async draftOutboundSms(contactId) {
    return this.request(`/api/contacts/${contactId}/outbound/sms/draft`, {
      method: 'POST',
    });
  }

  async sendOutboundSms(contactId, payload) {
    return this.request(`/api/contacts/${contactId}/outbound/sms/send`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async sendCalendarInvite(contactId, payload) {
    return this.request(`/api/contacts/${contactId}/outbound/calendar-invite`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ============================================
  // MANAGER COMMAND CENTER
  // ============================================

  async getManagerBriefing() {
    return this.request('/api/manager/briefing');
  }

  async getManagerInterventions() {
    return this.request('/api/manager/interventions');
  }

  async getTeamHealth() {
    return this.request('/api/manager/team-health');
  }

  async resolveIntervention(interventionId, action) {
    return this.request(`/api/manager/interventions/${interventionId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  async getManagerTrends(period = 'week') {
    return this.request(`/api/manager/trends?period=${period}`);
  }

  async redistributeLeads(fromUserId, toUserId, count = 5) {
    return this.request('/api/manager/leads/redistribute', {
      method: 'POST',
      body: JSON.stringify({ from_user_id: fromUserId, to_user_id: toUserId, count }),
    });
  }

  async getSalespersonDetail(userId) {
    return this.request(`/api/manager/salespeople/${userId}/detail`);
  }

  async getFunnel(period = 'month') {
    return this.request(`/api/manager/funnel?period=${period}`);
  }

  async getWinLoss(period = 'month') {
    return this.request(`/api/manager/win-loss?period=${period}`);
  }

  async getTeamComparison(period = 'month') {
    return this.request(`/api/manager/team-comparison?period=${period}`);
  }

  async createDeal(dealData) {
    return this.request('/api/deals/', {
      method: 'POST',
      body: JSON.stringify(dealData)
    });
  }

  async getDeals(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/api/deals/?${queryString}`);
  }

  // ============================================
  // DEALER KB + TONE (NEW)
  // ============================================

  async getDealerTone(dealerId) {
    return this.request(`/api/dealers/${dealerId}/tone`);
  }

  async updateDealerTone(dealerId, payload) {
    return this.request(`/api/dealers/${dealerId}/tone`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async listKbEntries(dealerId) {
    return this.request(`/api/dealers/${dealerId}/kb/entries`);
  }

  async createKbEntry(dealerId, payload) {
    return this.request(`/api/dealers/${dealerId}/kb/entries`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateKbEntry(dealerId, entryId, payload) {
    return this.request(`/api/dealers/${dealerId}/kb/entries/${entryId}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteKbEntry(dealerId, entryId) {
    return this.request(`/api/dealers/${dealerId}/kb/entries/${entryId}`, {
      method: 'DELETE',
    });
  }

  async listKbSources(dealerId) {
    return this.request(`/api/dealers/${dealerId}/kb/sources`);
  }

  async createKbSource(dealerId, payload) {
    return this.request(`/api/dealers/${dealerId}/kb/sources`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async deleteKbSource(dealerId, sourceId) {
    return this.request(`/api/dealers/${dealerId}/kb/sources/${sourceId}`, {
      method: 'DELETE',
    });
  }

  async crawlKbSource(dealerId, sourceId) {
    return this.request(`/api/dealers/${dealerId}/kb/sources/${sourceId}/crawl`, {
      method: 'POST',
    });
  }
  
  async uploadKnowledgeDoc(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = this._rehydrateTokenFromStorage();
    const url = `${API_URL}/api/admin/knowledge/upload`;

    const makeReq = (tkn) => fetch(url, {
      method: 'POST',
      headers: tkn ? { 'Authorization': `Bearer ${tkn}` } : {},
      body: formData,
    });

    let response = await makeReq(token);

    if (response.status === 401) {
      const token2 = this._rehydrateTokenFromStorage();
      this._devLog('[api] admin/knowledge/upload 401 retry-once', { hasToken: !!token2 });
      response = await makeReq(token2);
      if (response.status === 401) {
        throw new Error('Session expired, please log in again');
      }
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    
    return response.json();
  }
  
  async deleteKnowledgeDoc(docId) {
    return this.request(`/api/admin/knowledge/${docId}`, {
      method: 'DELETE',
    });
  }
  
  // Staff Management
  async getStaff() {
    return this.request('/api/admin/staff');
  }
  
  async updateStaffRole(userId, role) {
    return this.request(`/api/admin/staff/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  async updateStaffProfile(userId, profile) {
    return this.request(`/api/admin/staff/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(profile),
    });
  }
  
  async removeStaff(userId) {
    return this.request(`/api/admin/staff/${userId}`, {
      method: 'DELETE',
    });
  }
  
  async inviteStaff(email, fullName, role) {
    return this.request(`/api/auth/invite?email=${encodeURIComponent(email)}&full_name=${encodeURIComponent(fullName)}&role=${role}`, {
      method: 'POST',
    });
  }
  
  // Subscription
  async getSubscriptionInfo() {
    return this.request('/api/admin/subscription');
  }
  
  async upgradeSubscription(tier) {
    return this.request(`/api/admin/subscription/upgrade?tier=${tier}`, {
      method: 'POST',
    });
  }
  
  // DMS Connections
  async getDMSConnections() {
    return this.request('/api/admin/dms/connections');
  }
  
  async generateWebhookConfig(dmsType = 'webhook') {
    return this.request(`/api/admin/dms/webhook-config?dms_type=${dmsType}`, {
      method: 'POST',
    });
  }
  
  async getDMSSyncLogs(connectionId = null) {
    const query = connectionId ? `?connection_id=${connectionId}` : '';
    return this.request(`/api/admin/dms/sync-logs${query}`);
  }
  
  async getDMSSyncStatus() {
    return this.request('/api/admin/dms/sync-status');
  }

  // ============================================
  // LEADS ENDPOINTS
  // ============================================
  
  async getAvailableLeads(source = null) {
    const query = source ? `?source=${source}` : '';
    return this.request(`/api/leads/available${query}`);
  }
  
  async claimLead(leadId) {
    return this.request(`/api/leads/${leadId}/claim`, {
      method: 'POST',
    });
  }

  async allocateLead(leadId, salespersonId) {
    return this.request(`/api/leads/${leadId}/allocate?salesperson_id=${encodeURIComponent(salespersonId)}`, {
      method: 'POST',
    });
  }
  
  async unclaimLead(leadId) {
    return this.request(`/api/leads/${leadId}/unclaim`, {
      method: 'POST',
    });
  }
  
  async reassignLead(leadId, newUserId, reason = null) {
    const body = { new_user_id: newUserId };
    if (reason) body.reason = reason;
    return this.request(`/api/leads/${leadId}/reassign`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }
  
  async getMyPipeline(status = null) {
    const query = status ? `?status=${status}` : '';
    return this.request(`/api/leads/my-pipeline${query}`);
  }

  async getHotAvailableLeads() {
    return this.request('/api/leads/hot-available');
  }

  async getMyHotLeads() {
    return this.request('/api/leads/my-hot');
  }

  // ── S5: Autopilot view ──────────────────────────────────────────────────
  async getMyAutopilotJobs(status = null) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request(`/api/leads/my-autopilot-jobs${qs}`);
  }

  async getGlobalAutopilotJobs(status = null) {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.request(`/api/admin/autopilot/jobs${qs}`);
  }
  // ─────────────────────────────────────────────────────────────────────────
  
  async getLeadActivities(leadId) {
    return this.request(`/api/leads/${leadId}/activities`);
  }
  
  async getChatLeads() {
    return this.request('/api/leads/chat-source');
  }

  // ============================================
  // MANAGER ENDPOINTS
  // ============================================
  
  async getManagerDashboard() {
    return this.request('/api/manager/dashboard');
  }

  async getManagerBriefing() {
    return this.request('/api/manager/briefing');
  }

  async getManagerCommandCentreBriefing() {
    return this.request('/api/manager/command-centre/briefing');
  }

  async getManagerCommandCentreInterventions() {
    return this.request('/api/manager/command-centre/interventions');
  }

  async getManagerInterventions() {
    return this.request('/api/manager/interventions');
  }

  async resolveManagerIntervention(interventionId) {
    return this.request(`/api/manager/interventions/${interventionId}/resolve`, {
      method: 'POST',
    });
  }

  async getManagerTeamHealth() {
    return this.request('/api/manager/team-health');
  }

  async getManagerAppointments(params = {}) {
    const qs = new URLSearchParams();
    if (params.from) qs.set("from_date", params.from);
    if (params.to) qs.set("to_date", params.to);
    if (params.salesperson_id) qs.set("salesperson_id", params.salesperson_id);
    if (params.appointment_type) qs.set("appointment_type", params.appointment_type);
    if (params.status) qs.set("status", params.status);
    const q = qs.toString();
    return this.request(`/api/manager/appointments${q ? "?" + q : ""}`);
  }

  async createAppointment(data) {
    return this.request("/api/manager/appointments", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateAppointment(id, data) {
    return this.request(`/api/manager/appointments/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getManagerLiveBoard() {
    return this.request('/api/manager/live-board');
  }

  async setLeadBuyingWindow(leadId, enabled = true) {
    return this.request(`/api/leads/${leadId}/buying-window?enabled=${enabled}`, {
      method: 'POST',
    });
  }

  async addManagerNote(leadId, note, visibleToRep = true) {
    return this.request(`/api/leads/${leadId}/manager-note`, {
      method: 'POST',
      body: JSON.stringify({ note, visible_to_rep: visibleToRep }),
    });
  }
  
  async getAllLeads(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.source) params.append('source', filters.source);
    if (filters.claimed_by) params.append('claimed_by', filters.claimed_by);
    
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request(`/api/manager/leads/all${query}`);
  }
  
  async getStaffPerformance(days = 30) {
    return this.request(`/api/manager/staff/performance?days=${days}`);
  }
  
  async getUnclaimedPileup(threshold = 5) {
    return this.request(`/api/manager/alerts/unclaimed-pileup?threshold=${threshold}`);
  }
  
  // ============================================
  // KB V1 ENDPOINTS
  // ============================================
  
  async getKBSources() {
    return this.request('/api/kb/sources');
  }
  
  async createKBSource(source) {
    return this.request('/api/kb/sources', {
      method: 'POST',
      body: JSON.stringify(source),
    });
  }
  
  async deleteKBSource(sourceId) {
    return this.request(`/api/kb/sources/${sourceId}`, {
      method: 'DELETE',
    });
  }
  
  async getKBEntries(sourceId = null) {
    const query = sourceId ? `?source_id=${sourceId}` : '';
    return this.request(`/api/kb/entries${query}`);
  }
  
  async createKBEntry(entry) {
    return this.request('/api/kb/entries', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }
  
  async deleteKBEntry(entryId) {
    return this.request(`/api/kb/entries/${entryId}`, {
      method: 'DELETE',
    });
  }
  
  async queryKB(query, topK = 5, minConfidence = 0.7) {
    return this.request('/api/kb/query', {
      method: 'POST',
      body: JSON.stringify({
        query,
        top_k: topK,
        min_confidence: minConfidence,
      }),
    });
  }
  
  async uploadKBDocument(file) {
    const formData = new FormData();
    formData.append('file', file);
    
    const token = this._rehydrateTokenFromStorage();
    const url = `${API_URL}/api/kb/upload`;

    const makeReq = (tkn) => fetch(url, {
      method: 'POST',
      headers: tkn ? { 'Authorization': `Bearer ${tkn}` } : {},
      body: formData,
    });

    let response = await makeReq(token);

    if (response.status === 401) {
      const token2 = this._rehydrateTokenFromStorage();
      this._devLog('[api] kb/upload 401 retry-once', { hasToken: !!token2 });
      response = await makeReq(token2);
      if (response.status === 401) {
        throw new Error('Session expired, please log in again');
      }
    }
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }
    
    return response.json();
  }
  
  async getKBUploads() {
    return this.request('/api/kb/uploads');
  }
  
  async deleteKBUpload(uploadId) {
    return this.request(`/api/kb/uploads/${uploadId}`, {
      method: 'DELETE',
    });
  }

  // ============================================
  // DOCUMENTS ENDPOINTS
  // ============================================

  async getDocuments(params = {}) {
    const query = new URLSearchParams();
    if (params.search) query.append('search', params.search);
    if (params.category) query.append('category', params.category);
    if (params.tag) params.tag.forEach(t => query.append('tag', t));
    
    const queryString = query.toString();
    return this.request(`/api/documents${queryString ? `?${queryString}` : ''}`);
  }

  async getDocument(documentId) {
    return this.request(`/api/documents/${documentId}`);
  }

  async getDocumentDownloadUrl(documentId) {
    const base = process.env.REACT_APP_API_URL || '';
    return `${base}/api/documents/${documentId}/download`;
  }

  async uploadDocument({ file, category, description, tags }) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category || 'other');
    if (description) formData.append('description', description);
    if (tags?.length) formData.append('tags', tags.join(','));

    const token = this._rehydrateTokenFromStorage();
    const url = `${API_URL}/api/documents/upload`;

    const response = await fetch(url, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  async updateDocument(documentId, updates) {
    return this.request(`/api/documents/${documentId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteDocument(documentId) {
    return this.request(`/api/documents/${documentId}`, {
      method: 'DELETE',
    });
  }

  async generateDocumentShortLink(documentId, contactId = null) {
    const body = { document_id: documentId };
    if (contactId) body.contact_id = contactId;
    return this.request('/api/documents/short-link', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // ── Billing ──────────────────────────────────────────────────────────────

  async getBillingSummary() {
    return this.request('/api/billing/summary');
  }

  async syncBilling() {
    return this.request('/api/billing/sync', { method: 'POST' });
  }

  async updateBillingSettings(settings) {
    return this.request('/api/billing/settings', {
      method: 'PATCH',
      body: JSON.stringify(settings),
    });
  }

  async cancelBilling() {
    return this.request('/api/billing/cancel', { method: 'POST' });
  }

  async getBillingPortal() {
    return this.request('/api/billing/portal', { method: 'POST' });
  }

  async getBrands() {
    return this.request('/api/billing/brands');
  }

  async addBrand(name) {
    return this.request('/api/billing/brands', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async removeBrand(brandId) {
    return this.request(`/api/billing/brands/${brandId}`, { method: 'DELETE' });
  }
}

export const api = new ApiService();
export { ApiService };
